import type { ReactElement, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Card } from 'antd';

export interface WidgetContainerProps {
  title?: string;
  icon?: LucideIcon;
  iconColor?: string;
  children: ReactNode;
  headerRight?: ReactNode;
  minHeight?: number;
  noPadding?: boolean;
  className?: string;
  testId?: string;
}

export function WidgetContainer({
  title,
  icon: Icon,
  iconColor,
  children,
  headerRight,
  minHeight = 200,
  noPadding = false,
  className,
  testId,
}: WidgetContainerProps): ReactElement {
  return (
    <Card
      className={className ?? ''}
      data-testid={testId}
      title={
        title ? (
          <h2
            className="flex min-w-0 items-center gap-2 font-semibold"
            style={{
              color: 'var(--ant-color-text-secondary, #57534e)',
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              margin: 0,
            }}
          >
            {Icon && (
              <Icon
                size={13}
                strokeWidth={2}
                className="shrink-0"
                style={{ color: iconColor ?? 'var(--ant-color-text-tertiary, #a8a29e)' }}
              />
            )}
            <span className="truncate">{title}</span>
          </h2>
        ) : null
      }
      extra={headerRight}
      styles={{
        header: {
          padding: '9px 14px',
          borderBottom: '1px solid var(--ant-color-border-secondary, #e7e5e4)',
          minHeight: 'auto',
        },
        body: {
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          padding: noPadding ? 0 : 14,
        },
      }}
      style={{
        borderRadius: 'var(--ant-border-radius-lg, 14px)',
        background: 'var(--ant-color-bg-container, #fffdfa)',
        border: '1px solid var(--ant-color-border-secondary, #e7e5e4)',
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight,
        overflow: 'hidden',
      }}
    >
      {children}
    </Card>
  );
}
